import React, { useEffect, useState } from 'react'
import { Segmented } from 'antd'
import EChartComponent from '@/components/EChartComponent'
import { DatePicker, GetProps } from 'antd'
import useGlobalConfigStore from '@/stores/globalConfig'
import useStationStore from '@/stores/oneStation'
import useMessage from '@/hooks/useMessage'
import { calcVirtualByInterval, calcVirtualCumulantData } from '@/utils/getData'
import dayjs, { Dayjs } from 'dayjs'
import getTimeStamp from '@/utils/getTimeStamp'
import { useTranslation } from 'react-i18next'

type RangePickerProps = GetProps<typeof DatePicker.RangePicker>

const Chart: React.FC = () => {
  const { t } = useTranslation()
  const { message } = useMessage()
  const [chartOption, setChartOption] = useState<echarts.EChartsOption>({})
  const [timeInterval, setTimeInterval] = useState<string>('5min')
  const [timeType, setTimeType] = useState<string>('day')
  const [chooseTime, setChooseTime] = useState<Dayjs>(dayjs())
  const initFinished = useGlobalConfigStore(state => state.initFinished)
  const setLoading = useGlobalConfigStore(state => state.setLoading)
  const setUpdateTime = useGlobalConfigStore(state => state.setUpdateTime)
  const station_virtual_label = useStationStore(state => state.station_virtual_label)

  useEffect(() => {
    if (initFinished) {
      getDataAllStep()
    }
  }, [initFinished])

  const getDataAllStep = async () => {
    if (timeType === 'day') {
      await getDayData()
    } else {
      await getCumulantData()
    }
    setUpdateTime()
  }

  const getDayData = async () => {
    try {
      setLoading(true)
      let res: any,
        maxNum = 0,
        minNum = 0
      const option: echarts.EChartsOption = {
        color: ['orange', 'green', 'blue', 'brown', 'red'],
        tooltip: {
          trigger: 'axis',
        },
        dataZoom: [
          { type: 'inside' }, //用于添加滚轮缩放
        ],
        legend: {
          show: true,
          // bottom: 0,
          textStyle: {
            fontSize: 12,
            color: 'black',
          },
          inactiveColor: '#999999', // This sets the color for inactive legend items
          type: 'scroll',
        },

        xAxis: {
          type: 'time',
          splitNumber: 10,
          // },
        },
        series: [],
      }
      const { startTime, endTime } = getTimeStamp('day', chooseTime.toDate())
      for (let label of station_virtual_label) {
        for (let point of label.points) {
          if (point.property_code === 'P' || point.property_code === 'SOC') {
            if (point.property_code === 'P' && label.name === 'Battery') {
              continue
            }
            res = await calcVirtualByInterval(point, startTime, endTime, timeInterval, 'sampling')
            // console.log(res)
            ;(option.series as any[]).push({
              name: label.name + '-' + point.name,
              type: 'line',
              yAxisIndex: point.property_code === 'SOC' ? 1 : 0,
              data: res.data.map((item: any) => {
                if (point.property_code === 'P') {
                  item.value = item.value / 1000
                  if (!maxNum || maxNum < item.value) {
                    maxNum = item.value
                  }
                  if (!minNum || minNum > item.value) {
                    minNum = item.value
                  }
                }
                return [item.time, item.value]
              }),
              smooth: true,
              lineStyle: {
                width: 3,
              },
              symbol: 'none',
            })
          }
        }
      }
      option.yAxis = [
        {
          type: 'value',
          name: 'Power (kW)',
          position: 'left',
          minInterval: 15, // 设置最小间隔为20
          axisLabel: {
            fontSize: 12,
          },
          splitLine: {
            show: true,
          },
          max: maxNum,
          min: minNum,
        },
        {
          type: 'value',
          name: 'SOC (%)',
          position: 'right',
          axisLabel: {
            fontSize: 12,
          },
          splitLine: {
            show: false,
          },
          max: 100,
          min: Number(((100 * minNum) / maxNum).toFixed(0)),
        },
      ]
      setChartOption(option)
      setLoading(false)
    } catch (e) {
      console.log(e)
      setLoading(false)
      message.error('Get Chart Data Failed')
    }
  }

  const getCumulantData = async () => {
    try {
      setLoading(true)
      let res: any
      const option: echarts.EChartsOption = {
        legend: { orient: 'horizontal' },
        xAxis: { type: 'time' },
        yAxis: {
          name: 'kWh',
          nameTextStyle: {
            align: 'right',
            padding: [0, 0, 10, -30],
          },
          minInterval: 1,
          splitLine: { show: true },
        },
        series: [],
      }
      for (let label of station_virtual_label) {
        for (let point of label.points) {
          if (point.property_code === 'KA' || point.property_code === 'KRA') {
            res = await calcVirtualCumulantData([point], timeType, chooseTime.toDate())
            ;(option.xAxis as any).splitNumber = res.label.length
            ;(option.series as any[]).push({
              name: label.name + '-' + point.name,
              type: 'bar',
              barGap: 0,
              barCategoryGap: '30%',
              data: res.label.map((item: string, index: number) => [item, res.data[index]]),
            })
          }
        }
      }
      setChartOption(option)
      setLoading(false)
    } catch (e) {
      console.log(e)
      setLoading(false)
      message.error('Get Chart Data Failed')
    }
  }

  useEffect(() => {
    if (initFinished) {
      getDataAllStep()
    }
  }, [timeInterval, timeType, chooseTime])

  const disabledDate: RangePickerProps['disabledDate'] = current => {
    return current > dayjs().endOf('day')
  }

  return (
    <div id='charts'>
      <div className='charts-header'>
        <div className='charts-header-title'>{t('menu.chart')}</div>
        <Segmented
          defaultValue={timeType}
          options={[
            { label: t('common.daily'), value: 'day' },
            { label: t('common.weekly'), value: 'week' },
            { label: t('common.monthly'), value: 'month' },
            { label: t('common.yearly'), value: 'year' },
            { label: t('common.overyear'), value: 'overyear' },
          ]}
          onChange={setTimeType}
          style={{ marginLeft: '10px' }}
        />
      </div>
      <div className='charts-content'>
        <div className='charts-content-title'>
          <span>{t('site.realtimeChart')}</span>
          <div className='charts-content-title-right'>
            {timeType === 'day' && (
              <Segmented
                defaultValue={timeInterval}
                options={['5min', '15min', '30min', '60min']}
                onChange={setTimeInterval}
              />
            )}
            {timeType !== 'overyear' && (
              <DatePicker
                value={chooseTime}
                onChange={setChooseTime}
                format={
                  timeType === 'day'
                    ? 'DD-MM-YYYY'
                    : timeType === 'week'
                    ? 'YYYY-wo'
                    : timeType === 'month'
                    ? 'MM-YYYY'
                    : 'YYYY'
                }
                picker={
                  timeType === 'day' ? 'date' : timeType === 'week' ? 'week' : timeType === 'month' ? 'month' : 'year'
                }
                style={{ marginLeft: '10px' }}
                disabledDate={disabledDate}
              />
            )}
          </div>
        </div>
        <div className='charts-content-chart'>
          <EChartComponent staticOption={chartOption} />
        </div>
      </div>
    </div>
  )
}

export default Chart

// const { RangePicker } = DatePicker

// const debounce = (func: any, wait: any) => {
//   let timeout: any
//   return (...args: any) => {
//     clearTimeout(timeout)
//     timeout = setTimeout(() => func.apply(this, args), wait)
//   }
// }
// const Filter: React.FC = (props) => {
//   const Quick = [
//     "Last 2 days",
//     "Yesterday",
//     "Today",
//     "Last hour",
//     "Last 7 days",
//     "Day before",
//     "This week",
//     "Last 3 hours",
//     "Last 30 days",
//     "This day last week",
//     "This month",
//     "Last 6 hours",
//     "Last 90 days",
//     "Previous month",
//     "This year",
//     "Last 12 hours",
//     "Last 6 months",
//     "Previous week",
//     "Last 12 months",
//     "Last 24 hours",
//   ]

//   return (
//     <div className="Charts-filter">
//       <div className="Charts-filter-title">Quick ranges</div>
//       <Row gutter={[10, 10]}>
//         {Quick.map((v: any, k: any) => (
//           <Col span={6}>
//             <div className={`Charts-filter-item ${k == 0 ? "activity" : ""}`}>{v}</div>
//           </Col>
//         ))}
//       </Row>
//       <div className="Charts-filter-title">Custom</div>
//       <RangePicker format="DD-MM-YYYY" />
//     </div>
//   )
// }

// const Chart: React.FC = (props) => {
//   const [viewMode, setViewMode] = useState("Split")
//   const handleViewModeChange = (value: any) => {
//     setViewMode(value)
//   }

//   const chartRefs = useRef<(echarts.ECharts | null)[]>(Array(5).fill(null))
//   const [chartFull, setChartFull] = useState<any[]>([false, false, false, false, false])
//   const updateChartFull = (index: any, value: any, height: any) => {
//     setChartFull((prevChartFull) => {
//       const newChartFull = [...prevChartFull] // Create a copy of the current state
//       newChartFull[index] = value // Update the specific index
//       return newChartFull // Return the updated array
//     })
//     chartRefs.current.forEach((chart, i) => {
//       if (i == index && chart?.getDom()) {
//         if (value) {
//           chart.getDom().style.height = window.innerHeight - 120 + "px"
//         } else {
//           chart.getDom().style.height = height + "px"
//         }
//         chart.resize()
//       }
//     })
//   }

//   useEffect(() => {
//     const handleMouseOver = (params: any, currentIndex: number) => {
//       chartRefs.current.forEach((chart, index) => {
//         if (chart && index !== currentIndex) {
//           const { seriesIndex, dataIndex } = params

//           chart.dispatchAction({
//             type: dataIndex ? "showTip" : "hideTip",
//             seriesIndex,
//             dataIndex,
//           })
//         }
//       })
//     }
//     const debouncedHandleMouseOver = debounce((params: any, index: number) => {
//       handleMouseOver(params, index)
//     }, 50) // 200ms 防抖时间
//     setChartFull(chartFull)
//     chartRefs.current.forEach((chart, index) => {
//       if (chart) {
//         chart.off("updateAxisPointer") // 移除之前的事件监听器
//         chart.on("updateAxisPointer", (params) => {
//           debouncedHandleMouseOver(params, index)
//         })
//       }
//     })

//     return () => {
//       chartRefs.current.forEach((chart) => {
//         if (chart) {
//           chart.off("updateAxisPointer")
//           chart.dispose()
//         }
//       })
//     }
//   }, [chartFull, setChartFull])
//   const option: echarts.EChartsOption = {
//     tooltip: {
//       trigger: "axis",
//     },

//     legend: {
//       show: false,
//       data: [
//         "PV-Real-time Power",
//         "Battery-SOC",
//         "Load-Real-time Power",
//         "Grid-Real-time Power",
//         "Gen-Real-time Power",
//       ],
//       bottom: 0,

//       textStyle: {
//         fontSize: 12,
//         color: "#fff",

//         padding: [0, 0, 0, 5],
//       },
//       padding: [0, 10],

//       inactiveColor: "#999999", // This sets the color for inactive legend items

//       type: "scroll",
//     },
//     grid: {
//       top: 30,
//       left: 40,
//       bottom: 30,
//       right: 30,
//     },
//     xAxis: {
//       type: "category",
//       boundaryGap: false,
//       axisLabel: {
//         fontSize: 12,
//         formatter: (value: string) => value, // Format to show time directly
//       },
//       data: ["00:00", "01:00", "02:00", "03:00", "04:00", "05:00", "06:00", "07:00"],
//     },
//     yAxis: [
//       {
//         type: "value",
//         name: "Power (kW)",
//         position: "left",
//         minInterval: 15, // 设置最小间隔为20

//         axisLabel: {
//           fontSize: 12,
//         },
//         splitLine: {
//           show: true,
//         },
//       },
//       {
//         type: "value",
//         name: "SOC (%)",
//         position: "right",
//         axisLabel: {
//           fontSize: 12,
//         },
//         splitLine: {
//           show: false,
//         },
//       },
//     ],
//     series: [
//       {
//         name: "PV-Real-time Power",
//         type: "line",
//         yAxisIndex: 0,
//         data: [5, 10, 15, 20, 25, 30, 35, 40],
//         lineStyle: {
//           width: 2,
//         },
//         emphasis: {
//           focus: "series",
//         },
//         areaStyle: {
//           opacity: 0.3, // Use opacity to create transparency effect
//         },
//         smooth: true, // Enable smooth curve
//         symbol: "none",
//         // smooth: true, // Enable smooth curve
//         // symbol: "none",
//       },
//       {
//         name: "Battery-SOC",
//         type: "line",
//         yAxisIndex: 1,
//         data: [50, 55, 60, 65, 70, 75, 80, 85],
//         lineStyle: {
//           width: 2,
//         },
//         emphasis: {
//           focus: "series",
//         },
//         areaStyle: {
//           opacity: 0.3, // Use opacity to create transparency effect
//         },
//         smooth: true, // Enable smooth curve
//         symbol: "none",
//       },
//       {
//         name: "Load-Real-time Power",
//         type: "line",
//         yAxisIndex: 0,
//         data: [10, 15, 20, 25, 30, 35, 40, 45],
//         lineStyle: {
//           width: 2,
//         },
//         emphasis: {
//           focus: "series",
//         },
//         areaStyle: {
//           opacity: 0.3, // Use opacity to create transparency effect
//         },
//         smooth: true, // Enable smooth curve
//         symbol: "none",
//       },
//       {
//         name: "Grid-Real-time Power",
//         type: "line",
//         yAxisIndex: 0,
//         data: [7, 14, 21, 28, 35, 42, 49, 56],
//         lineStyle: {
//           width: 2,
//         },
//         emphasis: {
//           focus: "series",
//         },
//         areaStyle: {
//           opacity: 0.3, // Use opacity to create transparency effect
//         },
//         smooth: true, // Enable smooth curve
//         symbol: "none",
//       },
//       {
//         name: "Gen-Real-time Power",
//         type: "line",
//         yAxisIndex: 0,
//         data: [8, 16, 24, 32, 40, 48, 56, 64],
//         lineStyle: {
//           width: 2,
//         },
//         emphasis: {
//           focus: "series",
//         },
//         areaStyle: {
//           opacity: 0.3, // Use opacity to create transparency effect
//         },
//         smooth: true, // Enable smooth curve
//         symbol: "none",
//       },
//     ],
//   }
//   let grid: any = {
//     top: 30,
//     left: 40,
//     bottom: 30,
//     right: 30,
//   }
//   const option2 = { ...option, legend: { show: false }, grid: grid } //根据业务自定义
//   const option3 = { ...option, legend: { show: false }, grid: grid } //根据业务自定义
//   const option4 = { ...option, legend: { show: false }, grid: grid } //根据业务自定义
//   const option5 = { ...option, legend: { show: false }, grid: grid } //根据业务自定义

//   return (
//     <div id="Charts">
//       <div className="Charts-header">
//         <div className="Charts-header-title">
//           <div className="Charts-header-title-span">Advanced for Grid connected with ESS</div>
//           <div className="su"></div>
//           <Segmented
//             defaultValue="Split"
//             options={["Split", "Merge"]}
//             onChange={(value) => {
//               handleViewModeChange(value)
//             }}
//           />
//         </div>
//         <div className="Charts-header-rgith">
//           <div className="timer">
//             <img src="/assets/image/left.svg" />
//             <Popover trigger="click" placement="bottom" content=<Filter />>
//               <span>Last 90 days</span>
//             </Popover>
//             <img src="/assets/image/date.svg"></img>
//             <img src="/assets/image/aright.svg" />
//           </div>
//           <div className="charts-spance">
//             <Segmented defaultValue="5Min" style={{ marginBottom: 8 }} options={["5Min", "15Min", "30Min", "60Min"]} />
//           </div>
//         </div>
//       </div>
//       <div className="mergeChart" style={{ display: viewMode === "Merge" ? "" : "none" }}>
//         <div className={`Charts-content-one ${chartFull[0] ? "chart-full" : ""}`}>
//           <div className="charts-content-title">
//             <span>Charts-Name</span>
//           </div>
//           <EChartComponent
//             option={option}
//             onChartInit={(chartInstance) => {
//               chartRefs.current[0] = chartInstance
//             }}
//             style={{ height: window.innerHeight > 800 ? "690px" : "510px" }}
//           />
//         </div>
//       </div>
//       <div className="splitChart" style={{ display: viewMode === "Split" ? "" : "none" }}>
//         <div className={`Charts-content-one ${chartFull[0] ? "chart-full" : ""}`}>
//           <div className="charts-content-title">
//             <span>Charts-Name</span>
//             <img
//               src={`/assets/image/${!chartFull[0] ? "qp" : "fqp"}.svg`}
//               onClick={() => updateChartFull(0, !chartFull[0], 415)}
//             />
//           </div>
//           <EChartComponent
//             option={option}
//             onChartInit={(chartInstance) => {
//               chartRefs.current[0] = chartInstance
//             }}
//             style={{ height: "415px" }}
//           />
//         </div>

//         <Row gutter={[16, 16]}>
//           <Col span={chartFull[1] ? 24 : 16}>
//             <div className="chart2">
//               <div className="charts-content-title">
//                 <span>PV Inverter Yield [30]</span>
//                 <img
//                   src={`/assets/image/${!chartFull[1] ? "qp" : "fqp"}.svg`}
//                   onClick={() => updateChartFull(1, !chartFull[1], 201)}
//                 />
//               </div>
//               <EChartComponent
//                 option={option2}
//                 onChartInit={(chartInstance) => {
//                   chartRefs.current[1] = chartInstance
//                 }}
//                 style={{ height: "201px", width: "100%" }}
//               />
//             </div>
//           </Col>
//           <Col span={chartFull[1] ? 24 : 8}>
//             <div className="Charts-content-one-card">
//               <div className="Charts-content-one-card-title">PV Inverter Yield [30]</div>
//               <div className="Charts-content-one-card-content1">
//                 <div className="Charts-content-one-card-content1-title">Ac</div>
//                 <Row>
//                   <Col span={8}>247.3 V</Col>
//                   <Col span={8}>6.95 A</Col>
//                   <Col span={8}>1718 W</Col>
//                 </Row>
//               </div>
//               <div className="Charts-content-one-card-content1">
//                 <div className="Charts-content-one-card-content1-title">Energy</div>
//                 <Row>
//                   <Col span={8}>2820.00 kwh</Col>
//                 </Row>
//               </div>
//             </div>
//           </Col>
//         </Row>
//         <Row gutter={16}>
//           <Col span={chartFull[2] ? 24 : 8}>
//             <div className="charts-card">
//               <div className="charts-content-title">
//                 <span>PV Inverter Yield [30]</span>
//                 <img
//                   src={`/assets/image/${!chartFull[2] ? "qp" : "fqp"}.svg`}
//                   onClick={() => updateChartFull(2, !chartFull[2], 215)}
//                 />
//               </div>
//               <EChartComponent
//                 option={option2}
//                 onChartInit={(chartInstance) => {
//                   chartRefs.current[2] = chartInstance
//                 }}
//                 style={{ height: "215px", width: "100%" }}
//               />
//             </div>
//           </Col>
//           <Col span={chartFull[3] ? 24 : 8}>
//             <div className="charts-card">
//               <div className="charts-content-title">
//                 <span>PV Inverter Yield [30]</span>
//                 <img
//                   src={`/assets/image/${!chartFull[3] ? "qp" : "fqp"}.svg`}
//                   onClick={() => updateChartFull(3, !chartFull[3], 215)}
//                 />{" "}
//               </div>
//               <EChartComponent
//                 option={option2}
//                 onChartInit={(chartInstance) => {
//                   chartRefs.current[3] = chartInstance
//                 }}
//                 style={{ height: "215px", width: "100%" }}
//               />
//             </div>
//           </Col>
//           <Col span={chartFull[4] ? 24 : 8}>
//             <div className="charts-card">
//               <div className="charts-content-title">
//                 <span>PV Inverter Yield [30]</span>
//                 <img
//                   src={`/assets/image/${!chartFull[4] ? "qp" : "fqp"}.svg`}
//                   onClick={() => updateChartFull(4, !chartFull[4], 215)}
//                 />
//               </div>
//               <EChartComponent
//                 option={option2}
//                 onChartInit={(chartInstance) => {
//                   chartRefs.current[4] = chartInstance
//                 }}
//                 style={{ height: "215px", width: "100%" }}
//               />
//             </div>
//           </Col>
//         </Row>
//       </div>
//     </div>
//   )
// }
